import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { Icon } from '@wartek-id/icon'
import { Link } from '@wartek-id/link'
import { List, ListItem } from '@wartek-id/list'
import { Popover, PopoverContent, PopoverTrigger } from '@wartek-id/popover'
import { Text } from '@wartek-id/text'

import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { isKsDefinitifPlt } from 'app/KinerjaKSPage/utils/profile'
import { useAuth } from 'utils/auth'
import styleHeader from './PageHeader.module.css'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import KinerjaLogo from 'app/KinerjaShared/components/KinerjaLogo'

import type { SKPPelaksanaanTrackerParams } from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'

interface PemetaanKompetensiPageHeaderProps {
  title?: string
  icon?: string
  className?: string
  children?: React.ReactNode
}

const PageHeader = ({
  title = 'Pengelolaan Kinerja Guru',
  className = '',
  children,
}: PemetaanKompetensiPageHeaderProps) => {
  const { session } = useAuth()
  const router = useRouter()
  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

  const { profile: dataProfile } = useRoleContext()

  // Tracker
  useEffect(() => {
    if (isMenuOpen && dataProfile && isKsDefinitifPlt(dataProfile)) {
      let params: SKPPelaksanaanTrackerParams | null = null

      switch (router?.pathname) {
        case PENGELOLAAN_KINERJA_KS_PENILAI_PATH:
          params = {
            activity_type: 'penilaian_ks_home_click_cek_profil',
            screen_name: 'ks_penilaian_home',
            event_trigger: 'ks_penilaian_home',
          }
          break
        case `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/[npsn]`:
          params = {
            activity_type: 'penilaian_ks_hub_click_cek_profil',
            screen_name: 'ks_penilaian_hub',
            event_trigger: 'ks_penilaian_hub',
          }
      }

      if (params) {
        trackEvent(params)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenuOpen, dataProfile])

  return (
    <>
      <div
        className={clsx(
          'fixed top-0',
          'w-full z-20',
          'bg-surface-default shadow-header',
          'flex justify-between items-center',
          className
        )}
      >
        <div className="flex-none pt-3 pb-2.5 px-6">
          <Link
            className="!p-0 !leading-[0] flex items-center gap-3 !no-underline"
            href={`${process.env.NEXT_PUBLIC_BASE_URL}/pengelolaan-kinerja`}
          >
            <KinerjaLogo title={title} />
          </Link>
        </div>
        <div className="flex flex-none px-6">
          <Popover
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
          >
            <PopoverTrigger aria-label="Menu" className="flex gap-3 pt-6 pb-5">
              <Icon>group</Icon>
              <Text variant="action" className="shrink-0">
                {session?.user?.name}
              </Text>
              <Icon
                className={clsx(
                  'transition-transform transform',
                  isMenuOpen && 'rotate-180'
                )}
              >
                expand_more
              </Icon>
            </PopoverTrigger>

            <PopoverContent
              className={clsx(
                'transition-all mt-3 max-w-xs min-w-[20rem]',
                styleHeader.container
              )}
            >
              <List type="menu" className="!p-0">
                <ListItem
                  className={clsx(
                    '!p-4 bg-default flex flex-col rounded-t-lg',
                    styleHeader.listItem
                  )}
                >
                  <Text
                    as="div"
                    variant="action"
                    className="text-left overflow-wrap-anywhere"
                  >
                    {session?.user?.name}
                  </Text>
                  <Text
                    as="div"
                    color="subdued"
                    className="overflow-wrap-anywhere"
                  >
                    {session?.user?.loginEmail || session?.user?.email}
                  </Text>
                </ListItem>
                {children ? children : <></>}
                <ListItem
                  className={clsx('!px-4 !py-3', styleHeader.listItem)}
                  onClick={() => router.push('/logout')}
                >
                  <Text variant="body">Keluar</Text>
                </ListItem>
              </List>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </>
  )
}

export default PageHeader
